import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { instanciaService, MetaTemplate } from '../../services/instanciaService';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Search, CheckCircle, XCircle, Clock, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

interface MetaTemplateSendDialogProps {
  open: boolean;
  onClose: () => void;
  instanciaId: string;
  onSend: (templateName: string, languageCode: string, components: any[], previewText: string) => void;
  disabled?: boolean;
}

interface VariableField {
  key: string;
  componentType: string;
  componentIndex: number;
  parameterIndex: number;
  label: string;
  example: string;
  value: string;
}

interface PlaceholderToken {
  kind: 'number' | 'name';
  value: string;
}

const extractPlaceholders = (text: string): PlaceholderToken[] => {
  const tokens: PlaceholderToken[] = [];
  const seen = new Set<string>();
  const regex = /\{\{\s*(\d+|[A-Za-zÀ-ÿ_][\wÀ-ÿ]*)\s*\}\}/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const raw = match[1].trim();
    if (seen.has(raw)) continue;
    seen.add(raw);
    tokens.push({ kind: /^\d+$/.test(raw) ? 'number' : 'name', value: raw });
  }
  return tokens;
};

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  APPROVED: { icon: <CheckCircle className="h-3.5 w-3.5 text-green-500" />, color: 'bg-green-50 text-green-700 border-green-200' },
  REJECTED: { icon: <XCircle className="h-3.5 w-3.5 text-red-500" />, color: 'bg-red-50 text-red-700 border-red-200' },
  PENDING: { icon: <Clock className="h-3.5 w-3.5 text-yellow-500" />, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  DISABLED: { icon: <XCircle className="h-3.5 w-3.5 text-gray-400" />, color: 'bg-gray-50 text-gray-500 border-gray-200' },
  IN_DEVELOP: { icon: <Clock className="h-3.5 w-3.5 text-blue-500" />, color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const hasVariables = (template: MetaTemplate): boolean => {
  if (!template.components) return false;
  const varRegex = /\{\{\s*(?:\d+|[A-Za-zÀ-ÿ_][\wÀ-ÿ]*)\s*\}\}/;
  for (const comp of template.components) {
    if ((comp.type === 'BODY' || (comp.type === 'HEADER' && comp.format === 'TEXT')) && comp.text) {
      if (varRegex.test(comp.text)) return true;
    }
    if (comp.type === 'BUTTONS' && comp.buttons) {
      for (const btn of comp.buttons) {
        if (btn.type === 'URL' && btn.url && varRegex.test(btn.url)) return true;
      }
    }
  }
  return false;
};

const extractFields = (template: MetaTemplate, t: (key: string) => string): VariableField[] => {
  const extracted: VariableField[] = [];
  if (!template.components) return extracted;

  template.components.forEach((comp, compIdx) => {
    if (comp.type === 'BODY' && comp.text) {
      const placeholders = extractPlaceholders(comp.text);
      const maxNumeric = placeholders
        .filter(p => p.kind === 'number')
        .reduce((max, p) => Math.max(max, parseInt(p.value, 10) - 1), -1);
      let nextFree = maxNumeric + 1;
      placeholders.forEach((p, order) => {
        const parameterIndex = p.kind === 'number' ? parseInt(p.value, 10) - 1 : nextFree++;
        const example = comp.example?.body_text?.[0]?.[order] || '';
        extracted.push({
          key: p.value,
          componentType: 'body',
          componentIndex: 0,
          parameterIndex,
          label: `${t('templateSend.bodyVariableLabel')} {{${p.value}}}`,
          example,
          value: example,
        });
      });
    }

    if (comp.type === 'HEADER' && comp.format === 'TEXT' && comp.text) {
      const placeholders = extractPlaceholders(comp.text);
      const maxNumeric = placeholders
        .filter(p => p.kind === 'number')
        .reduce((max, p) => Math.max(max, parseInt(p.value, 10) - 1), -1);
      let nextFree = maxNumeric + 1;
      placeholders.forEach((p, order) => {
        const parameterIndex = p.kind === 'number' ? parseInt(p.value, 10) - 1 : nextFree++;
        const example = comp.example?.header_text?.[0]?.[order] || '';
        extracted.push({
          key: p.value,
          componentType: 'header',
          componentIndex: 0,
          parameterIndex,
          label: `${t('templateSend.headerVariableLabel')} {{${p.value}}}`,
          example,
          value: example,
        });
      });
    }

    if (comp.type === 'BUTTONS' && comp.buttons) {
      comp.buttons.forEach((btn, btnIdx) => {
        if (btn.type === 'URL' && btn.url) {
          const placeholders = extractPlaceholders(btn.url);
          const maxNumeric = placeholders
            .filter(p => p.kind === 'number')
            .reduce((max, p) => Math.max(max, parseInt(p.value, 10) - 1), -1);
          let nextFree = maxNumeric + 1;
          placeholders.forEach((p) => {
            const parameterIndex = p.kind === 'number' ? parseInt(p.value, 10) - 1 : nextFree++;
            extracted.push({
              key: p.value,
              componentType: 'button',
              componentIndex: btnIdx,
              parameterIndex,
              label: `${t('templateSend.buttonVariableLabel')} "${btn.text || t('templateSend.variableFallbackUrl')}" {{${p.value}}}`,
              example: '',
              value: '',
            });
          });
        }
      });
    }
  });

  return extracted;
};

const buildParameter = (f: VariableField): Record<string, string> => {
  const isNamed = !/^\d+$/.test(f.key);
  return isNamed
    ? { type: 'text', parameter_name: f.key, text: f.value }
    : { type: 'text', text: f.value };
};

const buildComponents = (fields: VariableField[]): any[] => {
  const components: any[] = [];
  const byType: Record<string, VariableField[]> = {};

  fields.forEach(f => {
    const key = f.componentType;
    if (!byType[key]) byType[key] = [];
    byType[key].push(f);
  });

  if (byType['body']) {
    const params = byType['body']
      .sort((a, b) => a.parameterIndex - b.parameterIndex)
      .map(buildParameter);
    components.push({ type: 'body', parameters: params });
  }

  if (byType['header']) {
    const params = byType['header']
      .sort((a, b) => a.parameterIndex - b.parameterIndex)
      .map(buildParameter);
    components.push({ type: 'header', parameters: params });
  }

  if (byType['button']) {
    const grouped: Record<number, VariableField[]> = {};
    byType['button'].forEach(f => {
      if (!grouped[f.componentIndex]) grouped[f.componentIndex] = [];
      grouped[f.componentIndex].push(f);
    });
    Object.entries(grouped).forEach(([idx, btnFields]) => {
      const sorted = btnFields.sort((a, b) => a.parameterIndex - b.parameterIndex);
      components.push({
        type: 'button',
        sub_type: 'url',
        index: parseInt(idx),
        parameters: sorted.map(buildParameter),
      });
    });
  }

  return components;
};

const MetaTemplateSendDialog: React.FC<MetaTemplateSendDialogProps> = ({
  open,
  onClose,
  instanciaId,
  onSend,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('APPROVED');
  const [selectedTemplate, setSelectedTemplate] = useState<MetaTemplate | null>(null);
  const [fields, setFields] = useState<VariableField[]>([]);

  const fetchTemplates = useCallback(async () => {
    if (!open || !instanciaId) return;
    setLoading(true);
    try {
      const res = await instanciaService.listarTemplates(instanciaId, { status: filterStatus });
      setTemplates(res.data.data || []);
    } catch (err: any) {
      console.error('Erro ao listar templates Meta:', err);
      toast({
        title: t('templateSend.fetchErrorTitle'),
        description: err.response?.data?.message || t('templateSend.fetchErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [open, instanciaId, filterStatus, toast]);

  useEffect(() => {
    if (open) {
      setSelectedTemplate(null);
      setFields([]);
      setSearch('');
      fetchTemplates();
    }
  }, [open, fetchTemplates]);

  useEffect(() => {
    if (selectedTemplate) {
      setFields(extractFields(selectedTemplate, t));
    }
  }, [selectedTemplate, t]);

  const handleSelectTemplate = (template: MetaTemplate) => {
    if (disabled) return;
    setSelectedTemplate(template);
  };

  const updateValue = (index: number, value: string) => {
    setFields(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value };
      return updated;
    });
  };

  const fillFor = (text: string, componentType: string): string => {
    return text.replace(/\{\{\s*(\d+|[A-Za-zÀ-ÿ_][\wÀ-ÿ]*)\s*\}\}/g, (match, raw) => {
      const key = raw.trim();
      const field = fields.find(f => f.componentType === componentType && f.key === key);
      return field?.value || match;
    });
  };

  const buildPreview = (): { header?: string; body?: string; buttons?: Array<{ text?: string; url?: string }> } => {
    if (!selectedTemplate?.components) return {};
    let header: string | undefined;
    let body: string | undefined;
    const buttons: Array<{ text?: string; url?: string }> = [];

    selectedTemplate.components.forEach(comp => {
      if (comp.type === 'HEADER' && comp.format === 'TEXT' && comp.text) {
        header = fillFor(comp.text, 'header');
      }
      if (comp.type === 'BODY' && comp.text) {
        body = fillFor(comp.text, 'body');
      }
      if (comp.type === 'BUTTONS' && comp.buttons) {
        comp.buttons.forEach(btn => {
          if (btn.url) buttons.push({ text: btn.text, url: fillFor(btn.url, 'button') });
          else if (btn.text) buttons.push({ text: btn.text });
        });
      }
    });

    return { header, body, buttons };
  };

  const preview = buildPreview();
  const hasPreviewContent = preview.header || preview.body || (preview.buttons && preview.buttons.length > 0);
  const hasEmptyVariables = fields.some(f => !f.value.trim());

  const handleSend = () => {
    if (hasEmptyVariables) {
      toast({
        title: t('templateSend.pendingVariablesTitle'),
        description: t('templateSend.pendingVariablesDesc'),
        variant: 'destructive',
      });
      return;
    }
    const components = fields.length > 0 ? buildComponents(fields) : [];
    const previewText = [preview.header, preview.body].filter(Boolean).join('\n\n') || `[Template: ${selectedTemplate?.name}]`;
    onSend(selectedTemplate!.name, selectedTemplate!.language, components, previewText);
  };

  const filtered = templates.filter(
    (tpl) =>
      tpl.name.toLowerCase().includes(search.toLowerCase()) ||
      tpl.language.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg grid gap-4 dark:bg-slate-800 dark:text-white">
        <DialogHeader>
          <DialogTitle className="dark:text-white">
            {selectedTemplate ? t('templateSend.sendTemplateTitle') : t('templateSend.selectTemplateTitle')}
          </DialogTitle>
          <DialogDescription className="dark:text-gray-400">
            {selectedTemplate
              ? `${t('templateSend.templateInfo')} ${selectedTemplate.name} (${selectedTemplate.language})`
              : t('templateSend.chooseTemplateDesc')}
          </DialogDescription>
        </DialogHeader>

        {!selectedTemplate ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-1">
                {['APPROVED', 'PENDING', 'DISABLED'].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilterStatus(s)}
                    className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                      filterStatus === s
                        ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                    }`}
                  >
                    {t(`templates.status.${s}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('templateSend.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
              {loading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('templateSend.loadingTemplates')}
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-4">
                  {templates.length === 0
                    ? t('templateSend.noTemplatesFound')
                    : t('templateSend.noTemplatesMatch')}
                </p>
              ) : (
                filtered.map((template) => {
                  const statusCfg = STATUS_CONFIG[template.status] || STATUS_CONFIG.PENDING;
                  const needsVars = hasVariables(template);
                  return (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-2 rounded-md border bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm dark:text-gray-200 truncate">
                            {template.name}
                          </span>
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border ${statusCfg.color}`}>
                            {statusCfg.icon}
                            {t(`templates.status.${template.status}`, template.status)}
                          </span>
                          {needsVars && (
                            <span className="text-xs px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700">
                              {t('templateSend.variablesBadge')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                          <span>{template.language}</span>
                          {template.category && (
                            <>
                              <span>·</span>
                              <span>{template.category}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <Send className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-3">
              {fields.length > 0 && (
                <div className="space-y-3">
                  {fields.map((field, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-sm dark:text-gray-300">{field.label}</Label>
                      <Input
                        value={field.value}
                        onChange={(e) => updateValue(idx, e.target.value)}
                        placeholder={field.example ? `${t('templateSend.examplePlaceholder')} ${field.example}` : t('templateSend.valuePlaceholder')}
                        className="dark:bg-gray-700 dark:text-white"
                        disabled={disabled}
                      />
                    </div>
                  ))}
                  {hasEmptyVariables && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {t('templateSend.fillAllVariablesHint')}
                    </p>
                  )}
                </div>
              )}

              {/* Preview da mensagem (estilo bolha WhatsApp) */}
              <div className="border-b border-gray-200 dark:border-slate-600" />
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                  <Send className="h-3 w-3" /> {t('templateSend.previewLabel')}
                </p>
                {hasPreviewContent ? (
                  <div className="max-w-full px-3 py-2 rounded-lg rounded-tr-none bg-[#d9fdd3] dark:bg-[#005c4b] border border-gray-200 dark:border-gray-600 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] space-y-1">
                    {preview.header && (
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 break-words">
                        {preview.header}
                      </p>
                    )}
                    {preview.body && (
                      <p className="text-sm text-gray-900 dark:text-gray-50 break-words whitespace-pre-wrap">
                        {preview.body}
                      </p>
                    )}
                    {preview.buttons && preview.buttons.length > 0 && (
                      <div className="pt-1.5 space-y-1">
                        {preview.buttons.map((btn, i) => (
                          <div
                            key={i}
                            className="px-3 py-1.5 rounded-full bg-blue-600/10 dark:bg-blue-500/20 border border-blue-300 dark:border-blue-600 text-xs font-medium text-blue-700 dark:text-blue-300 text-center"
                          >
                            {btn.text || btn.url}
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500 dark:text-gray-300 text-right mt-1">{t('templateSend.now')}</p>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg border border-dashed border-gray-300 dark:border-slate-500 text-sm text-gray-400 dark:text-gray-500">
                    {t('templateSend.noPreviewContent')}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={disabled}>
              {t('common.cancel')}
            </Button>
            {selectedTemplate && (
              <Button type="button" onClick={handleSend} disabled={disabled || hasEmptyVariables}>
                {disabled ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {t('common.send')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MetaTemplateSendDialog;
